import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	IconButton,
	InputAdornment,
	Snackbar,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useWeb3React } from "@web3-react/core";
import { useEffect, useState } from "react";
import { CONTRACT_NAMES, NETWORK_NAMES, networkConfig } from "../const";
import { DASHBOARD_PALETTE as dash } from "../utils/palette.token";
import {
	dashOutlinedTextFieldSx,
	dashPrimaryGradientButtonSx,
} from "../utils/muiDashboardSx";
import { connectMetamask, getContract, getContractOnlyRead } from "../wallet";
// import DateTimePicker from 'react-datetime-picker';

// type DefaultTimeType = 'today' | 'month' | 'year' | 'all'

export const RewardNFT = () => {
	const [rewardPoint, setRewardPoint] = useState<number | string>("");
	const [totalSupply, setTotalSupply] = useState<number>(0);
	const [loading, setLoading] = useState(false);
	const [copySuccess, setCopySuccess] = useState(false);
	const [snackbar, setSnackbar] = useState<{
		open: boolean;
		message: string;
		severity: "success" | "error";
	}>({ open: false, message: "", severity: "success" });
	const web3Object = useWeb3React();

	const handleAddRewardPoint = (e: any) => {
		const value = e.target.value;
		if (value === "" || (/^\d+$/.test(value) && Number(value) >= 0)) {
			setRewardPoint(value);
		}
	};

	const handleSubmitReward = async () => {
		if (
			!rewardPoint ||
			isNaN(Number(rewardPoint)) ||
			Number(rewardPoint) <= 0
		) {
			setSnackbar({
				open: true,
				message: "Please enter a valid positive number",
				severity: "error",
			});
			return;
		}
		setLoading(true);
		try {
			const contract = getContract(
				web3Object.account,
				CONTRACT_NAMES.RewardContract,
				true,
			);
			const responseReward = await contract.fundTalSupply(Number(rewardPoint));
			await responseReward.wait();
			setSnackbar({
				open: true,
				message: "Fund TAL successfully",
				severity: "success",
			});
			setRewardPoint("");
		} catch (error: any) {
			let message = "Something went wrong, please try again";

			// Try to extract the revert reason from various possible locations
			if (error?.data?.message) {
				message = error.data.message;
			} else if (error?.error?.message) {
				message = error.error.message;
			} else if (error?.message) {
				// If it's a generic JSON-RPC error, try to decode the revert reason
				if (
					error.message === "Internal JSON-RPC error." &&
					(error.data?.message || error.data)
				) {
					// Try to extract from error.data.message or decode from hex
					if (error.data?.message) {
						message = error.data.message;
					} else if (typeof error.data === "string") {
						const decoded = hexToAscii(error.data);
						if (decoded) {
							message = decoded;
						}
					}
				} else {
					message = error.message;
				}
			} else if (error?.data) {
				// Try to decode revert reason from hex if present
				try {
					const reason = hexToAscii(error.data);
					if (reason) message = reason;
				} catch {}
			}

			// Clean up the message for user display
			if (message.startsWith("execution reverted: ")) {
				message = message.replace("execution reverted: ", "");
			}
			setSnackbar({ open: true, message, severity: "error" });
		} finally {
			setLoading(false);
		}
	};

	const handleConnectWallet = async () => {
		setLoading(true);
		try {
			await connectMetamask(web3Object);
		} catch (e) {
			setSnackbar({
				open: true,
				message: "Failed to connect wallet",
				severity: "error",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleCopyAddress = () => {
		if (web3Object.account) {
			navigator.clipboard.writeText(web3Object.account);
			setCopySuccess(true);
			setTimeout(() => setCopySuccess(false), 1500);
		}
	};

	useEffect(() => {
		const getTotalSupply = async () => {
			try {
				const contract = getContractOnlyRead(
					CONTRACT_NAMES.RewardContract,
					NETWORK_NAMES.BSC,
					networkConfig[NETWORK_NAMES.BSC].MAINNET_CHAIN_ID,
				);
				const response = await contract.getTalSupply();
				setTotalSupply(Number(response));
			} catch (error) {
				console.log({ error });
				setSnackbar({
					open: true,
					message: "Failed to fetch total supply",
					severity: "error",
				});
			}
		};
		if (web3Object.account) {
			getTotalSupply();
		}
	}, [web3Object]);

	/** Khớp vùng main: Toolbar spacer (~64px) + padding main p:3 (24×2). Không dùng 100vh để tránh scroll kép. */
	const mainSlot = "calc(100dvh - 64px - 48px)" as const;

	return (
		<Box
			sx={{
				height: mainSlot,
				minHeight: 0,
				maxHeight: mainSlot,
				boxSizing: "border-box",
				bgcolor: dash.bg,
				color: dash.textPrimary,
				py: { xs: 1, sm: 1.5 },
				px: { xs: 2, sm: 3 },
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				overflow: "hidden",
			}}
		>
			<Box
				sx={{
					width: "100%",
					maxWidth: 720,
					maxHeight: "100%",
					borderRadius: 3,
					overflow: "auto",
					position: "relative",
					border: `1px solid ${dash.cardBorder}`,
					background: dash.gradientSurface,
					boxShadow: `0 24px 64px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(100, 160, 255, 0.06) inset`,
				}}
			>
				<Box
					sx={{
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						height: 3,
						background: `linear-gradient(90deg, ${dash.accent}, ${dash.success})`,
						opacity: 0.85,
					}}
				/>
				<Stack
					sx={{ p: { xs: 2.5, sm: 3, md: 3.5 }, pt: { xs: 3, sm: 3.5 } }}
					spacing={2.5}
				>
					<Stack
						direction="row"
						alignItems="center"
						spacing={1.5}
						sx={{ mb: 0.5 }}
					>
						<Box
							sx={{
								width: 4,
								height: 32,
								borderRadius: 1,
								bgcolor: dash.accent,
								flexShrink: 0,
								boxShadow: `0 0 12px ${dash.accentGlow}`,
							}}
						/>
						<Box>
							<Typography
								variant="h5"
								className="font-montserrat700"
								sx={{ color: dash.textPrimary, lineHeight: 1.25 }}
							>
								Reward TAL Management
							</Typography>
							<Typography
								variant="body2"
								sx={{ color: dash.textMuted, mt: 0.5 }}
							>
								Fund the reward pool on-chain
							</Typography>
						</Box>
					</Stack>

					{web3Object?.account ? (
						<Stack spacing={2.5}>
							<Stack
								direction="row"
								alignItems="center"
								justifyContent="space-between"
								flexWrap="wrap"
								gap={1.5}
								sx={{
									px: 2,
									py: 1.5,
									borderRadius: 2,
									bgcolor: dash.rowInset,
									border: `1px solid ${dash.cardBorder}`,
								}}
							>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									flexWrap="wrap"
								>
									<CheckCircleIcon sx={{ color: dash.success, fontSize: 22 }} />
									<Typography
										variant="body2"
										sx={{ color: dash.textSecondary, fontWeight: 600 }}
									>
										Connected
									</Typography>
									<Typography
										variant="body2"
										sx={{
											fontFamily: "ui-monospace, monospace",
											fontWeight: 500,
											color: dash.textPrimary,
										}}
									>
										{web3Object.account.slice(0, 6)}...
										{web3Object.account.slice(-4)}
									</Typography>
								</Stack>
								<Tooltip title={copySuccess ? "Copied!" : "Copy address"}>
									<IconButton
										size="small"
										onClick={handleCopyAddress}
										sx={{
											color: copySuccess ? dash.success : dash.muted,
											border: `1px solid ${dash.cardBorder}`,
											borderRadius: 1.5,
											"&:hover": {
												color: dash.accent,
												bgcolor: dash.accentDim,
											},
										}}
									>
										<ContentCopyIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Stack>

							<Box
								sx={{
									textAlign: "center",
									py: { xs: 1.5, sm: 2 },
									px: 2,
									borderRadius: 2,
									bgcolor: dash.cardBg,
									border: `1px solid ${dash.cardBorder}`,
								}}
							>
								<Typography
									variant="caption"
									sx={{
										color: dash.textMuted,
										letterSpacing: "0.12em",
										fontWeight: 600,
									}}
								>
									TOTAL TAL IN POOL
								</Typography>
								<Typography
									variant="h4"
									className="font-montserrat700"
									sx={{ color: dash.textPrimary, mt: 0.75, lineHeight: 1.1 }}
								>
									{totalSupply.toLocaleString()}
								</Typography>
							</Box>

							<Box>
								<Typography
									variant="subtitle2"
									sx={{ color: dash.textSecondary, mb: 1.5, fontWeight: 600 }}
								>
									Add reward TAL
								</Typography>
								<TextField
									type="number"
									sx={dashOutlinedTextFieldSx(dash)}
									value={rewardPoint}
									onChange={handleAddRewardPoint}
									InputProps={{
										endAdornment: (
											<InputAdornment position="end">TAL</InputAdornment>
										),
										inputProps: { min: 0 },
									}}
									fullWidth
									disabled={loading}
									placeholder="Enter amount"
								/>
							</Box>

							<Button
								onClick={handleSubmitReward}
								variant="contained"
								disableElevation
								disabled={loading}
								fullWidth
								sx={dashPrimaryGradientButtonSx(dash)}
							>
								{loading ? (
									<CircularProgress
										size={24}
										sx={{ color: dash.textPrimary }}
									/>
								) : (
									"Add reward"
								)}
							</Button>
						</Stack>
					) : (
						<Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
							<Typography
								variant="body1"
								align="center"
								sx={{ color: dash.textSecondary, maxWidth: 300 }}
							>
								Connect your wallet to view pool balance and fund TAL rewards.
							</Typography>
							<Button
								onClick={handleConnectWallet}
								variant="contained"
								disableElevation
								disabled={loading}
								sx={{
									...dashPrimaryGradientButtonSx(dash),
									minWidth: 220,
									px: 4,
								}}
							>
								{loading ? (
									<CircularProgress
										size={24}
										sx={{ color: dash.textPrimary }}
									/>
								) : (
									"Connect wallet"
								)}
							</Button>
						</Stack>
					)}
				</Stack>
			</Box>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={3000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
			>
				<Alert
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					severity={snackbar.severity}
					variant="filled"
					sx={{
						width: "100%",
						bgcolor: snackbar.severity === "error" ? dash.danger : dash.success,
						color: dash.textPrimary,
						"& .MuiAlert-icon": { color: "rgba(255,255,255,0.95)" },
					}}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Box>
	);
};

function hexToAscii(hex: string) {
	try {
		if (hex.startsWith("0x08c379a0")) {
			// Standard Solidity Error(string) selector
			const reasonHex = hex.slice(10 + 64, hex.length);
			return Buffer.from(reasonHex, "hex").toString("utf8").replace(/\0/g, "");
		}
	} catch {}
	return "";
}
